// Email templates — the receipt and invoice emails a merchant sends.
//
// These live in notification-service, which renders and delivers them; go-pim
// proxies the CRUD so the editing happens here. See src/api/emailTemplates.ts
// for why that split is structural rather than a preference.
//
// The three states this page has to handle well are all NORMAL, not errors, and
// each needs a different thing said:
//
//   501  the deployment has no notification-service admin credential — the
//        feature is off, nothing is broken, and sending is unaffected
//   400  the merchant has no notification_brand, so there is no per-merchant
//        template to edit. Fixable by the operator, so link them to where
//   ok    a template may exist or not; "not configured" means this merchant
//        currently uses the SHARED default, which is worth saying out loud
//        because saving here stops that and starts an override
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { EmptyState, Segmented, StatusPill } from "@/components/UI";
import { ConfirmDialog, type ConfirmRequest } from "@/components/ConfirmDialog";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import { useAuth } from "@/store/auth";
import { useCompanies } from "@/queries/useCompanies";
import {
  listEmailTemplates,
  saveEmailTemplate,
  TEMPLATE_VARIABLES,
  type EmailDocType,
  type EmailTemplate,
} from "@/api/emailTemplates";

const BLANK: EmailTemplate = {
  doc_type: "receipt",
  subject: "",
  html_body: "",
  plain_body: "",
  configured: false,
  channel_id: 0,
};

export function EmailTemplates() {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const status = useAuth((s) => s.status);
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();

  const [companyId, setCompanyId] = useState("");
  const activeCompanyId = companyId || companies[0]?.id || "";
  const [docType, setDocType] = useState<EmailDocType>("receipt");

  const query = useQuery({
    queryKey: ["email-templates", activeCompanyId],
    queryFn: () => listEmailTemplates(activeCompanyId),
    enabled: status === "authenticated" && activeCompanyId !== "",
    staleTime: 30_000,
    // 501 and 400 are settled answers about configuration, not transient
    // faults — retrying them just delays the message the operator needs.
    retry: false,
  });

  const stored = useMemo(
    () => query.data?.templates.find((t) => t.doc_type === docType) ?? BLANK,
    [query.data, docType],
  );

  const [draft, setDraft] = useState<EmailTemplate>(BLANK);
  useEffect(() => {
    setDraft({ ...stored, doc_type: docType });
  }, [stored, docType]);

  const dirty =
    draft.subject !== stored.subject ||
    draft.html_body !== stored.html_body ||
    draft.plain_body !== stored.plain_body;

  const save = useMutation({
    mutationFn: () =>
      saveEmailTemplate(activeCompanyId, docType, {
        subject: draft.subject,
        html_body: draft.html_body,
        plain_body: draft.plain_body,
        channel_id: draft.channel_id,
      }),
    onSuccess: async () => {
      addToast({
        title: stored.configured ? "Template saved" : "Template created",
        body: `${docType === "receipt" ? "Receipt" : "Invoice"} email for ${query.data?.brand}.`,
      });
      await qc.invalidateQueries({ queryKey: ["email-templates", activeCompanyId] });
    },
    onError: (err) =>
      addToast({
        title: "Could not save the template",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });

  // Switching merchant or doc type REPLACES the draft, so unsaved HTML would
  // vanish with no warning. Losing hand-written markup to a stray click is the
  // worst thing this page could do, so the switch is confirmed while dirty.
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const guard = (what: string, apply: () => void) => {
    if (!dirty) {
      apply();
      return;
    }
    setConfirm({
      title: "Discard unsaved changes?",
      body: `You have edited the ${docType} template without saving. Switching ${what} will discard those edits.`,
      confirmLabel: "Discard and switch",
      destructive: true,
      onConfirm: () => {
        apply();
        setConfirm(null);
      },
    });
  };

  const company = companies.find((c) => c.id === activeCompanyId);
  const errStatus = query.isError ? apiErrorStatus(query.error) : null;

  return (
    <div className="page">
      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />

      <div className="page-head">
        <div>
          <div className="eyebrow">Console · go-pim</div>
          <h1>Email templates</h1>
          <div className="sub">
            {query.data?.brand
              ? `Editing brand “${query.data.brand}”`
              : "The receipt and invoice emails each merchant sends"}
          </div>
        </div>
        <div className="actions">
          <select
            className="input scope-picker"
            value={activeCompanyId}
            onChange={(e) => {
              const next = e.target.value;
              guard("merchant", () => setCompanyId(next));
            }}
            aria-label="Merchant"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          <button
            className="btn accent"
            type="button"
            disabled={!dirty || save.isPending || query.isError}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>

      {companiesLoading || query.isLoading ? (
        <div className="card">
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading…
            </div>
          </div>
        </div>
      ) : companies.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<I.Building size={16} />}
            title="No merchants yet"
            body="Email templates belong to a merchant."
            action={
              <Link className="btn accent" to="/">
                Add a merchant
              </Link>
            }
          />
        </div>
      ) : errStatus === 501 ? (
        <div className="card">
          <EmptyState
            icon={<I.Settings size={16} />}
            title="Template editing is not switched on"
            body="This deployment has no notification-service admin credential, so templates cannot be read or written from here. Receipt delivery itself is unaffected — it uses a separate key and keeps working."
          />
        </div>
      ) : errStatus === 400 ? (
        // The merchant has no brand. Actionable, so point at the fix rather
        // than just reporting it: without a brand there is no per-merchant row,
        // and editing would otherwise hit the template shared by everyone.
        <div className="card">
          <EmptyState
            icon={<I.AlertCircle size={16} />}
            title={`${company?.display_name ?? "This merchant"} has no notification brand`}
            body="A brand is what gives a merchant its own email template. Without one it uses the shared default, and there is nothing here to edit."
            action={
              <Link className="btn accent" to={`/companies/${activeCompanyId}`}>
                Set one on the Delivery tab
              </Link>
            }
          />
        </div>
      ) : query.isError ? (
        <div className="card">
          <EmptyState
            icon={<I.AlertCircle size={16} />}
            title="Couldn't load templates"
            body={apiErrorMessage(query.error, "The request failed.")}
          />
        </div>
      ) : (
        <div className="row gap-16" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card" style={{ padding: 16 }}>
              <div
                className="row"
                style={{ justifyContent: "space-between", marginBottom: 16 }}
              >
                <Segmented<EmailDocType>
                  value={docType}
                  onChange={(next) => guard("document type", () => setDocType(next))}
                  label="Document type"
                  options={[
                    { value: "receipt", label: "Receipt" },
                    { value: "invoice", label: "Invoice" },
                  ]}
                />
                <div className="row gap-8" style={{ alignItems: "center" }}>
                  {dirty && (
                    <span className="dim" style={{ fontSize: 11 }}>
                      Unsaved changes
                    </span>
                  )}
                  <StatusPill status={stored.configured ? "active" : "pending"} />
                </div>
              </div>

              {!stored.configured && (
                <div
                  className="dim"
                  style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.55 }}
                >
                  This merchant has no {docType} template of its own yet, so it
                  currently uses the shared default. Saving here creates an
                  override that applies to this merchant only.
                </div>
              )}

              <div className="stack-16">
                <div className="field">
                  <label className="label" htmlFor="et-subject">
                    Subject
                  </label>
                  <input
                    id="et-subject"
                    className="input"
                    type="text"
                    value={draft.subject}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, subject: e.target.value }))
                    }
                    placeholder="Your {{ .doc_type }} {{ .number }}"
                  />
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    Line breaks are stripped before this reaches the mail
                    header.
                  </div>
                </div>

                <div className="field">
                  <label className="label" htmlFor="et-html">
                    HTML body
                  </label>
                  <textarea
                    id="et-html"
                    className="input mono"
                    rows={16}
                    value={draft.html_body}
                    spellCheck={false}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, html_body: e.target.value }))
                    }
                    placeholder="<p>Hi {{ .customer_name }}, here is your receipt.</p>"
                  />
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    Rendered with Go html/template. Values are auto-escaped, so
                    a customer name containing markup is shown as text, not
                    executed. Use inline styles — email clients ignore
                    stylesheets.
                  </div>
                </div>

                <div className="field">
                  <label className="label" htmlFor="et-plain">
                    Plain-text body <span className="opt">optional</span>
                  </label>
                  <textarea
                    id="et-plain"
                    className="input mono"
                    rows={6}
                    value={draft.plain_body}
                    spellCheck={false}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, plain_body: e.target.value }))
                    }
                  />
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    Shown by clients that refuse HTML. Not auto-escaped — it is
                    plain text, so there is nothing to escape into.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Variables an operator would otherwise have to guess at. */}
          <div className="card" style={{ width: 280, flexShrink: 0, padding: 16 }}>
            <h3 style={{ marginBottom: 4 }}>Available variables</h3>
            <p className="dim" style={{ fontSize: 11, marginBottom: 12 }}>
              Optional ones are omitted rather than sent empty, so guard them
              with <span className="mono">{"{{ if }}"}</span> or the row renders
              blank.
            </p>
            <div className="stack-6">
              {TEMPLATE_VARIABLES.map((v) => (
                <div key={v.name}>
                  <div className="mono" style={{ fontSize: 12 }}>
                    {`{{ .${v.name} }}`}
                    {!v.always && (
                      <span className="dim" style={{ marginLeft: 6 }}>
                        optional
                      </span>
                    )}
                  </div>
                  <div className="dim" style={{ fontSize: 11 }}>
                    {v.note}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
